import { AuthWrapper } from '@/components/Auth/AuthWrapper';
import { LayoutWithoutHeader } from '@/layouts/LayoutWithoutHeader';
import { LayoutWithSidebar } from '@/layouts/LayoutWithSidebar';
import { RootRedirect } from '@/utils/RootRedirect';
import { ForgotPassword } from '@/views/auth/ForgotPassword';
import { ResetPassword } from '@/views/auth/ResetPassword';
import { SignIn } from '@/views/auth/SignIn';
import { SignUp } from '@/views/auth/SignUp';
import { SocialLoginCallback } from '@/views/auth/SocialLoginCallback';
import { VerifyEmail } from '@/views/auth/VerifyEmail';
import { LinkAccessory } from '@/views/dashboard/Accessory/LinkAccessory';
import { CardsList } from '@/views/dashboard/Cards/CardsList';
import { UpdateCard } from '@/views/dashboard/Cards/UpdateCard';
import { CreateCompany } from '@/views/dashboard/Companies/CreateCompany';
import { ListCompanies } from '@/views/dashboard/Companies/ListCompanies';
import { UpdateCompany } from '@/views/dashboard/Companies/UpdateCompany';
import { CreateContact } from '@/views/dashboard/Contacts/CreateContact';
import { ListContacts } from '@/views/dashboard/Contacts/ListContacts';
import { UpdateContact } from '@/views/dashboard/Contacts/UpdateContact';
import { CreateEvent } from '@/views/dashboard/Events/CreateEvent';
import { EventDetails } from '@/views/dashboard/Events/EventDetails';
import { EventFeedback } from '@/views/dashboard/Events/EventFeedback';
import { EventsManagement } from '@/views/dashboard/Events/EventsManagement';
import { ListEvents } from '@/views/dashboard/Events/ListEvents';
import { UpdateEvent } from '@/views/dashboard/Events/UpdateEvent';
import { IntegrationsIndex } from '@/views/dashboard/Integrations/IntegrationsIndex';
import Pricing from '@/views/dashboard/Pricing/Pricing';
import SubscriptionSuccess from '@/views/dashboard/Pricing/SubscriptionSuccess';
import { QRCodeGenerator } from '@/views/dashboard/QRCode/QRCodeGenerator';
import { AdminStatistics } from '@/views/dashboard/Statistics/AdminStatistics';
import { CreateUser } from '@/views/dashboard/Users/CreateUser';
import { ListUsers } from '@/views/dashboard/Users/ListUsers';
import { Settings } from '@/views/dashboard/Users/Settings';
import { UpdateUser } from '@/views/dashboard/Users/UpdateUser';
import { Error } from '@/views/Error';
import { DigitalCard } from '@/views/guest/DigitalCard';
import { ScanAccessory } from '@/views/guest/ScanAccessory';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from 'react-router-dom';

export const appRouter = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<Error />}>
      <Route path="/" element={<RootRedirect />} />

      <Route path="/:lang" element={<AuthWrapper needCheckAuth={true} />}>
        {/* [GUEST] Auth */}
        <Route
          path=""
          element={
            <AuthWrapper isAuthRoute={true}>
              <LayoutWithoutHeader />
            </AuthWrapper>
          }
        >
          <Route index element={<SignIn />} />
          <Route path="sign-up" element={<SignUp />} />
          <Route path="verify-email" element={<VerifyEmail />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password/:token" element={<ResetPassword />} />
          <Route
            path="social-login/:provider"
            element={<SocialLoginCallback />}
          />
        </Route>

        {/* [GUEST] Accessory & Profile */}
        <Route element={<LayoutWithoutHeader />}>
          <Route path="accessory/:uuid" element={<ScanAccessory />} />
          <Route path="profile/:slug" element={<DigitalCard />} />
          <Route path="error" element={<Error />} />
        </Route>

        {/* [USER] Dashboard */}
        <Route
          path="dashboard"
          element={
            <AuthWrapper requiresAuth={true}>
              <LayoutWithSidebar />
            </AuthWrapper>
          }
        >
          {/* [USER] Redirect to cards */}
          <Route index element={<CardsList />} />
          {/* [ADMIN] Companies */}
          <Route path="companies">
            <Route
              path="create"
              element={
                <AuthWrapper allowedRoles={'admin'}>
                  <CreateCompany />
                </AuthWrapper>
              }
            />
            <Route
              index
              element={
                <AuthWrapper allowedRoles={'admin'}>
                  <ListCompanies />
                </AuthWrapper>
              }
            />
            <Route
              path=":id"
              element={
                <AuthWrapper allowedRoles={'admin'}>
                  <UpdateCompany />
                </AuthWrapper>
              }
            />
          </Route>
          <Route
            path="my-company"
            element={
              <AuthWrapper allowedRoles={['admin', 'manager']}>
                <UpdateCompany />
              </AuthWrapper>
            }
          />

          {/* [ADMIN] Users */}
          <Route path="users">
            <Route
              index
              element={
                <AuthWrapper allowedRoles={['admin', 'manager']}>
                  <ListUsers />
                </AuthWrapper>
              }
            />
            <Route
              path="create"
              element={
                <AuthWrapper allowedRoles={['admin', 'manager']}>
                  <CreateUser />
                </AuthWrapper>
              }
            />
            <Route
              path=":id"
              element={
                <AuthWrapper allowedRoles={['admin', 'manager']}>
                  <UpdateUser />
                </AuthWrapper>
              }
            />
          </Route>

          {/* [ADMIN] QR Code Generator */}
          <Route
            path="qrcode"
            element={
              <AuthWrapper allowedRoles={'admin'}>
                <QRCodeGenerator />
              </AuthWrapper>
            }
          />

          {/* [ADMIN] Events */}
          <Route path="events">
            <Route index element={<ListEvents />} />
            <Route path=":id" element={<EventDetails />} />
            <Route path="events-management">
              <Route index element={<EventsManagement />} />
              <Route path="create" element={<CreateEvent />} />
              <Route path=":id" element={<UpdateEvent />} />
            </Route>
            <Route path=":id/feedback" element={<EventFeedback />} />
          </Route>

          {/* [ADMIN] Statistics */}
          <Route path="statistics" element={<AdminStatistics />} />

          {/* [USER] Accessory */}
          <Route path="accessory">
            <Route path=":uuid/link" element={<LinkAccessory />} />
          </Route>

          {/* [USER] Settings */}
          <Route path="settings" element={<Settings />} />

          {/* [USER] Cards */}
          <Route path="cards/:slug" element={<UpdateCard />} />

          {/* [USER] Contacts */}
          <Route path="contacts" element={<ListContacts />} />
          <Route path="contacts/create" element={<CreateContact />} />
          <Route path="contacts/:id" element={<UpdateContact />} />

          {/* [USER] Integrations */}
          <Route path="integrations" element={<IntegrationsIndex />} />
        </Route>
        <Route path="dashboard" element={<AuthWrapper requiresAuth={true} />}>
          <Route path="pricing" element={<Pricing />} />
          <Route
            path="subscription-success"
            element={<SubscriptionSuccess />}
          />
        </Route>
      </Route>
    </Route>,
  ),
);
